import {
  IApiAchiever,
  IApiBooklet,
  IApiMediaPost,
  IApiRethinkArticle,
  IApiPreviewBlogPost,
  IApiSliderBlogPost,
  IApiSingleProductData,
} from '@/common/interfaces/api.interface';
import { IAchiever } from '@/common/interfaces/success-stories.interface';
import { IMediaPost } from '@/common/interfaces/media.interface';
import { IArticleCard } from '@/common/interfaces/article-card.interface';
import { IBooklet } from '@/common/interfaces/magazine.interface';
import { IPreviewBlogPost, ISliderBlogPost } from '@/common/interfaces/ringana-blog.interfaces';
import { ISingleProductData } from '../common/interfaces/products.interface';

export default class WpApiHelper {
  public static transformIApiAchieverToIAchiever(data: IApiAchiever): IAchiever {
    return {
      city: data?.place ?? ``,
      image: data?.image ?? ``,
      info: data?.text ?? ``,
      name: data?.title ?? ``,
      toggled: false,
    };
  }

  public static transformIApiAchieversToIAchievers(data: IApiAchiever[]): IAchiever[] {
    return data.map(item => this.transformIApiAchieverToIAchiever(item));
  }

  public static transformIApiMediaPostToIMediaPost(data: IApiMediaPost): IMediaPost {
    return {
      cover: data.cover ?? ``,
      date: data.date ?? ``,
      isDownload: !!data.download ?? false,
      link: (data.download || data.link) ?? ``,
      title: data.title ?? ``,
    };
  }

  public static transformIApiMediaPostsToIMediaPosts(data: IApiMediaPost[]): IMediaPost[] {
    return data.map(post => this.transformIApiMediaPostToIMediaPost(post));
  }

  public static transformIApiRethinkArticleToIArticleCard(data: IApiRethinkArticle): IArticleCard {
    return {
      backgroundImageLink: data?.articleImage ? `${data.articleImage}?w=367` : ``,
      hoverImageLink: data?.articleOverlayImage ? `${data.articleOverlayImage}?w=367` : ``,
      linkToArticle: data?.articleUrl ?? ``,
      text: data?.articleText ?? ``,
      title: data?.articleTitle ?? ``,
    };
  }

  public static transformIApiRethinkArticlesToIArticleCards(data: IApiRethinkArticle[]): IArticleCard[] {
    return data.map(item => this.transformIApiRethinkArticleToIArticleCard(item));
  }

  public static transformIApiBookletToIBooklet(data: IApiBooklet): IBooklet {
    return {
      bookletLink: data?.issueCode ?? ``,
      description: data?.intro ?? ``,
      edition: data?.edition ?? ``,
      heading: data?.title ?? ``,
    };
  }

  public static transformIApiBookletsToIBooklets(data: IApiBooklet[]): IBooklet[] {
    return data.map(item => this.transformIApiBookletToIBooklet(item));
  }

  public static transformIApiSliderBlogPostToISliderBlogPost(data: IApiSliderBlogPost): ISliderBlogPost {
    return {
      backgroundImg: data?.blogPostSliderImg ?? ``,
      link: data?.blogPostPermalink ?? ``,
      subtext: data?.blogPostSubheadline ?? ``,
      title: data?.blogPostTitle ?? ``,
    };
  }

  public static transformIApiSliderBlogPostsToISliderBlogPosts(data: IApiSliderBlogPost[]): ISliderBlogPost[] {
    return data.map(item => this.transformIApiSliderBlogPostToISliderBlogPost(item));
  }

  public static transformIApiPreviewBlogPostToIPreviewBlogPost(data: IApiPreviewBlogPost): IPreviewBlogPost {
    return {
      link: data?.blogPostPermalink ?? ``,
      previewImg: data?.blogPostPreviewImg ?? ``,
      title: data?.blogPostTitle ?? ``,
    };
  }

  public static transformIApiPreviewBlogPostsToIPreviewBlogPosts(data: IApiPreviewBlogPost[]): IPreviewBlogPost[] {
    return data.map(item => this.transformIApiPreviewBlogPostToIPreviewBlogPost(item));
  }

  public static transformIApiSingleProductDataToISingleProductData(data: IApiSingleProductData, imagesBaseUrl: string): ISingleProductData {
    return {
      awards: {
        beatTheMicrobead: {
          image: `${imagesBaseUrl}beat-the-microbead.svg`,
          link: data.productAwardBeatTheMicrobeadLink,
          received: data.productAwardBeatTheMicrobead,
          translationName: `zeroProducts`,
        },
        codecheck: {
          image: `${imagesBaseUrl}label-codechecker.svg`,
          link: data.productAwardCodecheckLink,
          received: data.productAwardCodecheck,
          translationName: `codecheck`,
        },
        cosmeticAnalysis: {
          image: `${imagesBaseUrl}label-cosmeticanalysis.png`,
          link: data.productAwardCosmeticAnalysisLink,
          received: data.productAwardCosmeticAnalysis,
          translationName: `cosmeticAnalysis`,
        },
        cosmosNatural: {
          image: `${imagesBaseUrl}label-cosmos-natural.png`,
          received: data.productAwardCosmosNatural,
          translationName: `cosmosNatural`,
        },
        cosmosOrganic: {
          image: `${imagesBaseUrl}label-cosmos-organic.png`,
          received: data.productAwardCosmosOrganic,
          translationName: `cosmosOrganic`,
        },
        ecocert: {
          image: `${imagesBaseUrl}label-organic-cosmetic.png`,
          received: data.productAwardEcocert,
          translationName: `ecocert`,
        },
        ecocertNaturkosmetik: {
          image: `${imagesBaseUrl}label-natural-cosmetic.png`,
          received: data.productAwardEcocertNatural,
          translationName: `ecocertNaturkosmetik`,
        },
        koelner: {
          image: `${imagesBaseUrl}label-koelner-liste.png`,
          received: data.productAwardKoelnerListe,
          translationName: `koelner`,
        },
        lacon: {
          image: `${imagesBaseUrl}label-lacon.png`,
          received: data.productAwardLacon,
          translationName: `lacon`,
        },
        patentiert: {
          image: `${imagesBaseUrl}label-patentiert-de.png`,
          received: data.productAwardPatented,
          translationName: `patentiert`,
        },
        umweltzeichen: {
          image: `${imagesBaseUrl}label-umweltzeichen.png`,
          received: data.productAwardUmweltzeichen,
          translationName: `umweltzeichen`,
        },
        uvaProtection: {
          image: `${imagesBaseUrl}label-UVA.svg`,
          received: data.productAwardUvaProtected,
          translationName: `uvaProtection`,
        },
      },
      category: data.category,
      categoryDynamicText: ``,
      image: data.image,
      interestingArticles: [
        {
          external: data.articleTeaserOneExternal ?? ``,
          headline: data.articleTeaserOneHeadline ?? ``,
          image: data.articleTeaserOneImage ?? ``,
          link: data.articleTeaserOneLink ?? ``,
          text: data.articleTeaserOneText ?? ``,
        }, {
          external: data.articleTeaserTwoExternal ?? ``,
          headline: data.articleTeaserTwoHeadline ?? ``,
          image: data.articleTeaserTwoImage ?? ``,
          link: data.articleTeaserTwoLink ?? ``,
          text: data.articleTeaserTwoText ?? ``,
        }, {
          external: data.articleTeaserThreeExternal ?? ``,
          headline: data.articleTeaserThreeHeadline ?? ``,
          image: data.articleTeaserThreeImage ?? ``,
          link: data.articleTeaserThreeLink ?? ``,
          text: data.articleTeaserThreeText ?? ``,
        },
      ],
      matchcode: data.matchcode,
      productBannerData: {
        banner: data.productBanner,
        text: data.productBannerText,
        textColor: data.productBannerTextColor,
        textHeadline: data.productBannerTextHeadline,
        textLeft: data.productBannerTextLeft,
      },
      productVideoData: {
        poster: data.productVideoPoster,
        video: data.productVideo,
        videoOgv: data.productVideoOgv,
        videoWebm: data.productVideoWebm,
      },
      shortDescription: data.shortInfo,
      shortDescriptionMail: ``,
      tabsData: {
        description: data.productDescription,
        howTo: data.productHowTo,
        image: data.image,
        ingredients: data.productIngredients,
        knowHow: data.productKnowHow,
        statutoryInfos: data.productStatutoryInfos,
        statutoryTable: data.productStatutoryTable,
      },
      title: data.title,
      type: data.productType,
      usp: data.usp,
      wpProductId: data.wordpressID?.toString() ?? ``,
    };
  }
}
